/**
 * PATH: packages/email-hub/src/feature/hooks/useSendEmailToInbox.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USESENDEMAILTOINBOX-001
 * PURPOSE: Canonical body of this email hooks module. Lifted
 *          from apps/<tenant>/src/features/email/hooks/useSendEmailToInbox.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/hooks/useSendEmailToInbox.ts: thin re-export
 *     shim that points back here.
 */

import { useCallback, useState } from 'react'

import { emailApiFetch } from '../lib/api'

const QUOTECH_BACKEND =
  (import.meta.env.VITE_QUOTECH_BACKEND as string | undefined) ??
  'https://quotech-backend.quotech.ai'

// Relative path — the shared email client prepends the backend origin.
const INBOX_SEND_PATH = '/api/kanban/inbox/send-email/'

export interface SendEmailToInboxInput {
  emailId: string
  organisation: number
}

export interface SendEmailToInboxResult {
  /** The Card row (from the BE's CardSerializer). Shape is the
   *  master Kanban Card; we don't import the universal type here
   *  to keep the dependency surface tight. Callers that need the
   *  full type can cast or import from @quotech/ui/kanban. */
  card: { id: string; title: string; lane: string }
  /** True when the BE returned 201 (new card created), false when
   *  it returned 200 (card already existed — idempotent path). */
  created: boolean
}

export class SendEmailToInboxError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'SendEmailToInboxError'
    this.status = status
  }
}

/** Pure I→O: build the absolute endpoint URL. Kept for callers/tests that
 *  want the canonical URL; the request itself goes through ``emailApiFetch``
 *  with the relative path (which prepends the same origin). */
export function buildSendEmailToInboxUrl(): string {
  const trimmed = QUOTECH_BACKEND.replace(/\/+$/, '')
  return `${trimmed}${INBOX_SEND_PATH}`
}

/**
 * Send an email to the user's personal Inbox board.
 *
 * Routes through the shared credentialed email client (``emailApiFetch``)
 * instead of a bespoke ``fetch`` + manual cookie-token extraction, so it
 * inherits the same auth/credentials + tenant handling as every other
 * email-hub call (FU2 resilience). Throws ``SendEmailToInboxError`` with the
 * HTTP status on any non-2xx — callers branch on ``.status`` to render the
 * right toast (e.g. 404 → "Set up your inbox first").
 */
export async function sendEmailToInbox(
  input: SendEmailToInboxInput,
): Promise<SendEmailToInboxResult> {
  let response: Response
  try {
    response = await emailApiFetch(INBOX_SEND_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email_id: input.emailId,
        organisation: input.organisation,
      }),
    })
  } catch (err) {
    // Transport failure (offline, DNS, CORS) — surface as status 0 so the
    // caller renders the generic "couldn't reach the server" toast.
    throw new SendEmailToInboxError(
      0,
      err instanceof Error ? err.message : 'Network error',
    )
  }

  if (response.status === 200 || response.status === 201) {
    const card = await response.json()
    return { card, created: response.status === 201 }
  }

  let detail = `Request failed (${response.status})`
  try {
    const body = await response.json()
    if (body?.detail) detail = String(body.detail)
  } catch {
    /* response wasn't JSON; keep the generic message */
  }
  throw new SendEmailToInboxError(response.status, detail)
}

/** React wrapper — `mutate(input)` + `pending` + `error` state.
 *  Mirrors the @tanstack/react-query useMutation surface without
 *  pulling QueryClient context (this hook is used inside the
 *  email preview pane which already lives inside react-query). */
export function useSendEmailToInbox() {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<SendEmailToInboxError | null>(null)

  const mutate = useCallback(
    async (input: SendEmailToInboxInput): Promise<SendEmailToInboxResult> => {
      setPending(true)
      setError(null)
      try {
        return await sendEmailToInbox(input)
      } catch (err) {
        const wrapped =
          err instanceof SendEmailToInboxError
            ? err
            : new SendEmailToInboxError(
                0,
                err instanceof Error ? err.message : 'Unknown error',
              )
        setError(wrapped)
        throw wrapped
      } finally {
        setPending(false)
      }
    },
    [],
  )

  return { mutate, pending, error }
}
